/**
 * Entity mutation hooks using generic factory
 */

import { postMessage } from '@/lib/realtime/broadcast'
import { scopedRecordKey } from '@/lib/realtime/record-query-scope'
import { getEntityTerminologyStatic } from '@/lib/terminology/static-terminology'
import { entityApi } from '@/modules/entities'
import { useOrganizationStore } from '@/store/organization.store'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { CreateEntity, Entity, UpdateEntity } from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { entityModuleCountsKey } from '../useEntityModuleCounts'
import { useWorkspaceSession } from '../useWorkspaceSession'
import { createResourceMutations } from './createResourceMutations'

function entityMutations(scope: ReturnType<typeof useWorkspaceSession>) {
  async function run<T>(event: string, request: () => Promise<T>, id?: string) {
    const organizationId =
      useOrganizationStore.getState().currentOrganization?.id
    const result = await scope.run(new AbortController().signal, request)
    scope.assertUnchanged()
    const record = result as
      | { id?: string; organizationId?: string }
      | undefined
    postMessage(event, {
      id: id ?? record?.id,
      organizationId: record?.organizationId ?? organizationId,
      eventId: crypto.randomUUID(),
    })
    return result
  }
  return createResourceMutations<Entity, CreateEntity, UpdateEntity>({
    resourceName: 'Entity',
    resourceNameSingular: 'entity',
    getLabels: () => getEntityTerminologyStatic(),
    queryKeyFactory: {
      ...queryKeys.entities,
      lists: () => scopedRecordKey(queryKeys.entities.lists(), scope),
      detail: (id: string) =>
        scopedRecordKey(queryKeys.entities.detail(id), scope),
    },
    replaceUpdatedRows: true,
    api: {
      create: (data: CreateEntity) =>
        run('entity:created', () => entityApi.create(data)),
      update: (id: string, data: UpdateEntity) =>
        run('entity:updated', () => entityApi.update(id, data), id),
      updateField: (id: string, field: string, value: unknown) =>
        run(
          'entity:updated',
          () => entityApi.updateEntityField({ entityId: id, field, value }),
          id,
        ),
      delete: (id: string) =>
        run('entity:deleted', () => entityApi.delete(id), id),
    },
    enableRealtime: false,
    invalidation: {
      onCreate: { invalidateRelated: [[...entityModuleCountsKey]] },
      onDelete: { invalidateRelated: [[...entityModuleCountsKey]] },
    },
  })
}
export function useCreateEntityMutation() {
  return entityMutations(useWorkspaceSession()).useCreateMutation()
}
export function useUpdateEntityMutation() {
  return entityMutations(useWorkspaceSession()).useUpdateMutation()
}
export function useUpdateEntityFieldMutation() {
  return entityMutations(useWorkspaceSession()).useUpdateFieldMutation!()
}
export function useDeleteEntityMutation() {
  return entityMutations(useWorkspaceSession()).useDeleteMutation()
}

/**
 * Bulk delete entities mutation hook
 */
export function useBulkDeleteEntitiesMutation() {
  const queryClient = useQueryClient()
  const scope = useWorkspaceSession()

  return useMutation({
    mutationFn: (ids: string[]) =>
      scope.run(new AbortController().signal, () =>
        entityApi.bulkDeleteEntities(ids),
      ),
    onSuccess: result => {
      const { singular, plural } = getEntityTerminologyStatic()
      toast.success(
        `${result.deletedCount} ${result.deletedCount === 1 ? singular : plural} deleted`,
      )
      // Invalidate entities list and module counts
      queryClient.invalidateQueries({
        queryKey: scopedRecordKey(queryKeys.entities.lists(), scope),
      })
      queryClient.invalidateQueries({ queryKey: entityModuleCountsKey })
    },
    onError: error => {
      toast.error(`Failed to delete ${getEntityTerminologyStatic().plural}`)
      console.error('Bulk delete entities error:', error)
    },
  })
}
